import React, { useState, useRef } from 'react';

// ── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, size = '' }) {
  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${size}`}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Confirm dialog ───────────────────────────────────────────────────────────
export function ConfirmModal({ message, onConfirm, onClose }) {
  return (
    <Modal title="Confirm" onClose={onClose} size="modal-sm">
      <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>{message}</p>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger" onClick={() => { onConfirm(); onClose(); }}>Delete</button>
      </div>
    </Modal>
  );
}

// ── Badge helper ─────────────────────────────────────────────────────────────
const STATUS_CLASSES = {
  // Generic
  'Active': 'badge-green', 'On Hold': 'badge-amber', 'Closed': 'badge-gray', 'Completed': 'badge-green',
  // Incident
  'Open': 'badge-red', 'Under Investigation': 'badge-amber',
  // MRQ / Doc
  'Draft': 'badge-gray', 'Pending': 'badge-amber', 'Approved': 'badge-green',
  'Ordered': 'badge-blue', 'Received': 'badge-green', 'Rejected': 'badge-red',
  'For Review': 'badge-blue', 'Under Review': 'badge-blue', 'Superseded': 'badge-gray',
  // VO
  'Pending': 'badge-amber',
  // IPC
  'Certified': 'badge-blue', 'Paid': 'badge-green', 'Disputed': 'badge-red',
  // Severity
  'Low': 'badge-gray', 'Medium': 'badge-amber', 'High': 'badge-red', 'Critical': 'badge-red',
  // Priority
  'Urgent': 'badge-red', 'Normal': 'badge-blue',
};

export function StatusBadge({ value }) {
  const cls = STATUS_CLASSES[value] || 'badge-gray';
  return <span className={`badge ${cls}`}>{value}</span>;
}

// ── Progress bar ─────────────────────────────────────────────────────────────
export function ProgressBar({ value, max = 100, color }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const colorClass = color || (pct > 90 ? 'progress-red' : pct > 70 ? 'progress-amber' : 'progress-blue');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div className="progress" style={{ flex: 1 }}>
        <div className={`progress-fill ${colorClass}`} style={{ width: pct + '%' }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

// ── Chip filter row ───────────────────────────────────────────────────────────
export function ChipFilter({ options, value, onChange }) {
  return (
    <div className="chip-row">
      {options.map(o => (
        <button key={o} className={`chip ${value === o ? 'active' : ''}`} onClick={() => onChange(o)}>{o}</button>
      ))}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
export function Empty({ icon = '📭', message = 'No records found.', action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 14, marginBottom: action ? 16 : 0 }}>{message}</div>
      {action}
    </div>
  );
}

// ── Metric card ───────────────────────────────────────────────────────────────
export function MetricCard({ label, value, sub, color }) {
  return (
    <div className="metric-card">
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={color ? { color } : {}}>{value}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

// ── Page header ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <div className="page-subtitle">{subtitle}</div>}
      </div>
      {children && <div className="header-actions">{children}</div>}
    </div>
  );
}

// ── JSON/file import drop zone ────────────────────────────────────────────────
export function ImportDropZone({ onFile, accept = '.json', label = 'Drop JSON file here or click to browse' }) {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  return (
    <div
      className={`drop-zone ${drag ? 'drag-over' : ''}`}
      onClick={() => ref.current.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
    >
      <div className="drop-zone-icon">📂</div>
      <div className="drop-zone-text">{label}</div>
      <div className="drop-zone-sub">Supports: {accept}</div>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); }} />
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────
export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, fontFamily: 'var(--font)', fontWeight: 500,
          color: active === t.id ? 'var(--blue-text)' : 'var(--text-muted)',
          borderBottom: `2px solid ${active === t.id ? 'var(--accent)' : 'transparent'}`,
          marginBottom: -1, transition: 'all 0.12s',
        }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Alert ─────────────────────────────────────────────────────────────────────
export function Alert({ type = 'info', children }) {
  return <div className={`alert alert-${type}`}>{children}</div>;
}
